import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { DataService } from 'src/app/services/data.service';
import {
  PrizeWeightFiltersStateService,
  PrizeWeightRow,
  PrizeWeightSectionKey,
} from '../services/prize-weight-filters-state.service';
import {
  MAX_OCCURRENCES_PER_CATEGORY_IN_DAILY_POOL,
  MAX_OCCURRENCES_PER_CATEGORY_IN_REMAINING_POOL,
} from '../services/prize-guardrails.constants';

interface CategoryOption {
  _id: string;
  name: string;
}

const REQUIRED_TOTAL = 100;

/**
 * A single weight-table filter section — a list of category/weight rows that must total 100%.
 * Reused for both "Daily Prize Weights" and "Remaining Prize Weights" on the Generate panel: each
 * instance is configured via [sectionKey]/[sectionTitle] and reads/writes its own independent
 * slice of PrizeWeightFiltersStateService (each section validates to 100% independently, not
 * combined). Every row change is published to that state immediately, which is what
 * GeneratePrizesPanelComponent's submit guards (weight-total-must-equal-100%, at-least-1-category)
 * read from.
 */
@Component({
  selector: 'app-prize-weights-filter',
  templateUrl: './prize-weights-filter.component.html',
  styleUrls: ['./prize-weights-filter.component.sass'],
})
export class PrizeWeightsFilterComponent implements OnInit, OnDestroy {
  @Input() sectionKey: PrizeWeightSectionKey;
  @Input() sectionTitle: string;

  readonly requiredTotal = REQUIRED_TOTAL;

  categoryOptions: CategoryOption[] = [];
  rowsForm: UntypedFormArray;

  private formValueSub: Subscription;

  constructor(
    private fb: UntypedFormBuilder,
    private dataService: DataService,
    private weightFiltersState: PrizeWeightFiltersStateService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.rowsForm = this.fb.array(
      this.weightFiltersState.getRows(this.sectionKey).map((row) => this.buildRowGroup(row)),
    );
    this.publishRows();
    this.formValueSub = this.rowsForm.valueChanges.subscribe(() => this.publishRows());

    const categories = await this.dataService.getAllOfTypeAsync('categories', {
      query: {},
      autopopulate: false,
      virtuals: false,
      sort: { name: 1 },
    });
    this.categoryOptions = (categories || []).map((category: any) => ({ _id: category._id, name: category.name }));
    this.resolveUnmatchedCategoryRefs();
  }

  ngOnDestroy(): void {
    this.formValueSub?.unsubscribe();
  }

  get rows(): UntypedFormGroup[] {
    return (this.rowsForm?.controls as UntypedFormGroup[]) || [];
  }

  get total(): number {
    return Math.round(this.rows.reduce((sum, row) => sum + (Number(row.get('weight')!.value) || 0), 0) * 100) / 100;
  }

  get isBalanced(): boolean {
    return this.total === this.requiredTotal;
  }

  /** Daily and Remaining pools cap a category's occurrences independently (CMS-6210) — surfaced here since it's otherwise invisible after the Filters drawer (which used to show it) was removed. */
  get maxOccurrencesPerCategory(): number {
    return this.sectionKey === 'dailyPrizeWeights'
      ? MAX_OCCURRENCES_PER_CATEGORY_IN_DAILY_POOL
      : MAX_OCCURRENCES_PER_CATEGORY_IN_REMAINING_POOL;
  }

  addCategory(): void {
    this.rowsForm.push(
      this.buildRowGroup({ id: this.generateRowId(), categoryRef: null, categoryName: '', weight: 0 }),
    );
    this.publishRows();
  }

  removeCategory(index: number): void {
    this.rowsForm.removeAt(index);
    this.publishRows();
  }

  onCategorySelected(row: UntypedFormGroup): void {
    const selected = this.categoryOptions.find((option) => option._id === row.get('categoryRef')!.value);
    row.get('categoryName')!.setValue(selected ? selected.name : '', { emitEvent: false });
  }

  private buildRowGroup(row: PrizeWeightRow): UntypedFormGroup {
    return this.fb.group({
      id: [row.id],
      categoryRef: [row.categoryRef, Validators.required],
      categoryName: [row.categoryName],
      weight: [row.weight, [Validators.required, Validators.min(0), Validators.max(100)]],
    });
  }

  private publishRows(): void {
    const rows: PrizeWeightRow[] = this.rowsForm.value;
    this.weightFiltersState.setRows(
      this.sectionKey,
      rows.map((row) => ({ ...row, weight: Number(row.weight) || 0 })),
    );
  }

  /** Resolves each default row's free-text label to a real categories._id once the list loads. */
  private resolveUnmatchedCategoryRefs(): void {
    this.rows.forEach((row) => {
      if (row.get('categoryRef')!.value) {
        return;
      }
      const label = (row.get('categoryName')!.value || '').trim();
      if (!label) {
        return;
      }
      const match = this.categoryOptions.find((option) => this.normalizeName(option.name) === this.normalizeName(label));
      if (match) {
        row.get('categoryRef')!.setValue(match._id, { emitEvent: false });
        row.get('categoryName')!.setValue(match.name, { emitEvent: false });
      }
    });
  }

  private normalizeName(value: string): string {
    return value.trim().toLowerCase().replace(/s$/, '');
  }

  private generateRowId(): string {
    return `${this.sectionKey}-row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }
}
